import { useEffect, useState, type FormEvent } from "react";
import type { AccountStatus, GeographicScope, RoleCode } from "@rbim/shared";
import { ShieldCheck } from "lucide-react";
import { apiRequest, ApiRequestError } from "../api";

type MunicipalityOption = {
  id: string;
  name: string;
  type: "MUNICIPALITY" | "CITY";
  psgcCode: string;
  provinceName?: string;
};
type BarangayOption = { id: string; municipalityId: string; name: string; psgcCode: string };
type GeographyResponse = {
  municipality?: MunicipalityOption | null;
  municipalities?: MunicipalityOption[];
  barangays: BarangayOption[];
};
type RbacTestSurface = "BARANGAY_HYBRID" | "BARANGAY_WEB" | "MUNICIPALITY_WEB" | "MAYOR_WEB" | "SYSTEM_PORTAL";
type RbacTestAccountState = "CURRENT_ACCOUNT" | "NOT_CREATED" | "EXISTING" | "CONFLICT";
type RbacTestAccountDescriptor = {
  role: RoleCode;
  displayName: string;
  username: string;
  scope: GeographicScope;
  locationLabel: string;
  testSurface: RbacTestSurface;
  state: RbacTestAccountState;
  accountId: string | null;
  status: AccountStatus | null;
};
type RbacTestAccountsResponse = {
  enabled: boolean;
  createdRoles: RoleCode[];
  resetRoles: RoleCode[];
  accounts: RbacTestAccountDescriptor[];
};

export function RbacTestAccountsPanel({
  geography,
  deploymentMunicipalityId,
}: {
  geography: GeographyResponse;
  deploymentMunicipalityId: string;
}) {
  const municipalities = municipalityOptions(geography);
  const deploymentMunicipality = municipalities.find((item) => item.id === deploymentMunicipalityId);
  const [barangayId, setBarangayId] = useState(
    geography.barangays.find((item) => item.municipalityId === deploymentMunicipalityId)?.id ?? "",
  );
  const [temporaryPassword, setTemporaryPassword] = useState("");
  const [data, setData] = useState<RbacTestAccountsResponse | null>(null);
  const [message, setMessage] = useState("");
  const [messageKind, setMessageKind] = useState<"info" | "error">("info");
  const [loading, setLoading] = useState(false);
  const barangays = geography.barangays.filter((item) => item.municipalityId === deploymentMunicipalityId);

  useEffect(() => {
    if (!deploymentMunicipalityId || !barangayId) {
      setData(null);
      return;
    }

    let cancelled = false;
    setLoading(true);
    setMessage("");
    setMessageKind("info");
    const query = new URLSearchParams({ municipalityId: deploymentMunicipalityId, barangayId });
    void apiRequest<RbacTestAccountsResponse>(`/system/rbac-test-accounts?${query.toString()}`)
      .then((response) => {
        if (!cancelled) setData(response);
      })
      .catch((error) => {
        if (!cancelled) { setMessageKind("error"); setMessage(errorMessage(error)); }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => { cancelled = true; };
  }, [deploymentMunicipalityId, barangayId]);

  async function prepareAccounts(event: FormEvent) {
    event.preventDefault();
    if (!deploymentMunicipalityId || !barangayId) return;
    setMessage("");
    setMessageKind("info");
    setLoading(true);
    try {
      const response = await apiRequest<RbacTestAccountsResponse>("/system/rbac-test-accounts", {
        method: "POST",
        body: JSON.stringify({ municipalityId: deploymentMunicipalityId, barangayId, temporaryPassword }),
      });
      setData(response);
      const parts = [];
      if (response.createdRoles.length) parts.push(`${response.createdRoles.length} created`);
      if (response.resetRoles.length) parts.push(`${response.resetRoles.length} access reset`);
      setMessage(parts.length
        ? `RBAC test accounts are ready: ${parts.join(", ")}. The current System Administrator account was left unchanged.`
        : "RBAC test accounts are ready. The current System Administrator account was left unchanged.");
    } catch (error) {
      setMessageKind("error");
      setMessage(errorMessage(error));
    } finally {
      setLoading(false);
    }
  }

  return (
    <section className="form-card rbac-test-card" aria-labelledby="rbac-test-heading">
      <div className="rbac-test-card-heading">
        <span className="rbac-test-card-icon"><ShieldCheck /></span>
        <div>
          <span className="eyebrow">Development / testing</span>
          <h2 id="rbac-test-heading">RBAC test accounts</h2>
          <p>Prepare deterministic test accounts for every active RBIM role without changing normal production provisioning authority.</p>
        </div>
      </div>

      <div className="rbac-test-body">
        <div className="rbac-test-guardrail">
          This workflow is restricted to the Municipality/City pinned to this Server API. Barangay Encoder uses one identity for RBIM Desktop and the online Barangay portal; the System Administrator uses your current account.
        </div>

        <form className="rbac-test-controls" onSubmit={prepareAccounts}>
          <label>Deployment Municipality / City
            <select value={deploymentMunicipalityId} disabled>
              <option value={deploymentMunicipalityId}>{deploymentMunicipality?.name ?? "Current server deployment"}</option>
            </select>
          </label>
          <label>Test Barangay
            <select value={barangayId} onChange={(event) => setBarangayId(event.target.value)} required disabled={!barangays.length}>
              {!barangays.length && <option value="">No Barangay available</option>}
              {barangays.map((item) => <option key={item.id} value={item.id}>{item.name}</option>)}
            </select>
          </label>
          <label>Shared temporary password
            <input
              type="password"
              autoComplete="new-password"
              minLength={12}
              maxLength={512}
              required
              value={temporaryPassword}
              onChange={(event) => setTemporaryPassword(event.target.value)}
              disabled={!data?.enabled}
              placeholder="Minimum 12 characters"
            />
          </label>
          <button className="primary-button rbac-test-action" disabled={!data?.enabled || loading || !barangayId}>
            {loading ? "Preparing..." : "Prepare all test accounts"}
          </button>
        </form>

        {data && !data.enabled && (
          <div className="rbac-test-disabled" role="status">
            Test provisioning is disabled. Set <code>RBIM_ENABLE_RBAC_TEST_PROVISIONING=true</code> in the Server API development/test environment and restart the API. Production rejects this setting.
          </div>
        )}
        {message && <div className={messageKind === "error" ? "form-error" : "rbac-test-message"} role="status">{message}</div>}

        <div className="responsive-table rbac-test-table">
          <table>
            <thead><tr><th>Role</th><th>Test surface</th><th>Expected access</th><th>Username</th><th>Assigned location</th><th>State</th></tr></thead>
            <tbody>
              {data?.accounts.map((account) => (
                <tr key={account.role}>
                  <td><strong>{formatRole(account.role)}</strong><small>{account.displayName}</small></td>
                  <td>{formatSurface(account.testSurface)}</td>
                  <td><small>{expectedAccess(account.role)}</small></td>
                  <td><code>@{account.username}</code></td>
                  <td>{account.locationLabel}</td>
                  <td>
                    <span className={`badge ${account.state === "CURRENT_ACCOUNT" || (account.state === "EXISTING" && account.status === "ACTIVE") ? "available" : ""}`}>
                      {formatState(account)}
                    </span>
                  </td>
                </tr>
              ))}
              {!data?.accounts.length && <tr><td colSpan={6}>{loading ? "Loading RBAC test-account status..." : "Select a valid deployment Barangay to inspect test accounts."}</td></tr>}
            </tbody>
          </table>
        </div>
      </div>
    </section>
  );
}

function municipalityOptions(geography: GeographyResponse) {
  if (geography.municipalities?.length) return geography.municipalities;
  return geography.municipality ? [geography.municipality] : [];
}

function formatRole(value: RoleCode) {
  if (value === "MUNICIPAL_CITY_ADMINISTRATOR") return "Municipality/City Administrator";
  if (value === "MUNICIPAL_CITY_USER") return "Municipality/City User";
  if (value === "MAYOR_VIEWER") return "Mayor Viewer";
  if (value === "BARANGAY_WEB_PORTAL") return "Legacy Barangay Web Portal";
  return value.split("_").map((part) => part[0] + part.slice(1).toLowerCase()).join(" ");
}

function formatSurface(value: RbacTestSurface) {
  if (value === "BARANGAY_HYBRID") return "RBIM Desktop + Barangay Web Portal";
  if (value === "BARANGAY_WEB") return "Barangay Supervisor Portal";
  if (value === "MUNICIPALITY_WEB") return "Municipality Web Portal";
  if (value === "MAYOR_WEB") return "Mayor Viewer Portal";
  return "System / Technical Portal";
}

function expectedAccess(role: RoleCode) {
  if (role === "BARANGAY_ENCODER") return "One own-Barangay identity for online encoding plus authorized hybrid Desktop/offline synchronization; no review or account administration.";
  if (role === "BARANGAY_WEB_PORTAL") return "Legacy compatibility role only; new accounts must use Barangay Encoder.";
  if (role === "BARANGAY_SUPERVISOR") return "Own-Barangay review, approve/return, resident updates, legacy import, reports, and LIRA; no account administration or permanent resident deletion.";
  if (role === "MUNICIPAL_CITY_USER") return "Municipality/City monitoring plus authorized data entry and correction across its Barangays; no deletion or User Management.";
  if (role === "MUNICIPAL_CITY_ADMINISTRATOR") return "Municipality/City governance, operational User Management, device enrollment, monitoring, correction, and governed deletion.";
  if (role === "MAYOR_VIEWER") return "Municipality/City dashboard, household/individual viewing, and reports only; no add, edit, delete, user/device administration, import, or LIRA.";
  if (role === "SYSTEM_ADMINISTRATOR") return "All RBIM application capabilities within approved server/geographic boundaries; sensitive actions remain audited.";
  return "Technical monitoring, audit, backup, synchronization, and settings; no normal resident-data workflows.";
}

function formatState(account: RbacTestAccountDescriptor) {
  if (account.state === "CURRENT_ACCOUNT") return "Current account";
  if (account.state === "NOT_CREATED") return "Not created";
  if (account.state === "CONFLICT") return "Username conflict";
  return account.status === "ACTIVE" ? "Ready" : `Existing - ${formatRoleCode(account.status ?? "UNKNOWN")}`;
}

function formatRoleCode(value: string) {
  return value.split("_").map((part) => part[0] + part.slice(1).toLowerCase()).join(" ");
}

function errorMessage(error: unknown) {
  return error instanceof ApiRequestError ? error.message : "The RBAC test-account request could not be completed.";
}
